// src/pages/OrganisatieDetail.js

import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";

export default function OrganisatieDetail() {
  const params = useParams();
  const id = params.id;

  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [contactsError, setContactsError] = useState(null);

  // ✅ Open Taken/Notities met filter op deze organisatie
  function openNotesTasksForOrg() {
    const p = new URLSearchParams();
    p.set("organisation_id", String(org?.id || id));
    window.location.href = `/notities-taken?${p.toString()}`;
  }

  // 1) Organisatie ophalen
  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        const url = "/api/organisations/" + id;
        const res = await fetch(url);

        if (!res.ok) {
          throw new Error("HTTP " + res.status);
        }

        const data = await res.json();
        if (!cancelled) setOrg(data);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return function cleanup() {
      cancelled = true;
    };
  }, [id]);

  // 2) Contactpersonen ophalen
  useEffect(() => {
    let cancelled = false;

    async function loadContacts() {
      try {
        setContactsLoading(true);
        setContactsError(null);

        const res = await fetch("/api/organisations/" + id + "/contacts");
        if (!res.ok) throw new Error("HTTP " + res.status);

        const data = await res.json();
        if (!cancelled) setContacts(data);
      } catch (e) {
        if (!cancelled) setContactsError(e.message || String(e));
      } finally {
        if (!cancelled) setContactsLoading(false);
      }
    }

    loadContacts();

    return function cleanup() {
      cancelled = true;
    };
  }, [id]);

  // 3) Scherm-states
  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Organisatie</h2>
        <div>Laden…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Organisatie</h2>
        <div style={{ color: "crimson" }}>Fout: {error}</div>
        <div style={{ marginTop: 12 }}>
          <Link to="/organisaties">← Terug naar organisaties</Link>
        </div>
      </div>
    );
  }

  if (!org) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Organisatie</h2>
        <div>Organisatie niet gevonden.</div>
        <div style={{ marginTop: 12 }}>
          <Link to="/organisaties">← Terug naar organisaties</Link>
        </div>
      </div>
    );
  }

  // 4) Normale pagina
  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
        <div>
          <h1 style={{ margin: 0 }}>{org.name}</h1>
          <div style={{ opacity: 0.75, marginTop: 6 }}>
            ID: {org.id} · Type: {org.type || "-"} · Regio: {org.region || "-"}
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button
            onClick={openNotesTasksForOrg}
            style={{ padding: "8px 12px" }}
            title="Open Taken/Notities voor deze organisatie"
          >
            📝 Taken/Notities
          </button>

          <Link to="/organisaties">← Terug</Link>
        </div>
      </div>

      <hr style={{ margin: "16px 0" }} />

      <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 10 }}>
        <div>
          <strong>Naam</strong>
        </div>
        <div>{org.name || "-"}</div>

        <div>
          <strong>Type</strong>
        </div>
        <div>{org.type || "-"}</div>

        <div>
          <strong>Regio</strong>
        </div>
        <div>{org.region || "-"}</div>

        <div>
          <strong>Website</strong>
        </div>
        <div>
          {org.website ? (
            <a href={org.website} target="_blank" rel="noreferrer">
              {org.website}
            </a>
          ) : (
            "-"
          )}
        </div>

        <div>
          <strong>Opmerking</strong>
        </div>
        <div style={{ whiteSpace: "pre-wrap" }}>{org.remarks || "-"}</div>
      </div>

      <hr style={{ margin: "16px 0" }} />

      <h2 style={{ marginTop: 0 }}>Contactpersonen</h2>

      {contactsLoading && <div>Laden…</div>}

      {contactsError && <div style={{ color: "crimson" }}>Fout: {contactsError}</div>}

      {!contactsLoading && !contactsError && contacts.length === 0 && <div>Nog geen contactpersonen.</div>}

      {contacts.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Naam</th>
              <th style={{ textAlign: "left" }}>Functie</th>
              <th style={{ textAlign: "left" }}>E-mail</th>
              <th style={{ textAlign: "left" }}>Telefoon</th>
              <th style={{ textAlign: "left" }}>Primair</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id}>
                <td>
                  {c.first_name} {c.last_name}
                </td>
                <td>{c.role || "-"}</td>
                <td>{c.email || "-"}</td>
                <td>{c.phone || c.mobile || "-"}</td>
                <td>{c.is_primary ? "Ja" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
